import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Accessible label for the dialog. */
  ariaLabel?: string;
  /** Panel width cap; defaults to the Figma 879px. */
  maxWidthPx?: number;
  /** Surface background utility for the panel body (token-based → dark/light). Defaults to the inset canvas. */
  surfaceClassName?: string;
  /**
   * Pixels between the viewport top and the panel top (e.g. the 48px topbar for the Accounts panel,
   * which slides in UNDER the bar instead of covering it). Default 0 = full height, as before.
   */
  insetTopPx?: number;
  /**
   * `false` renders a NON-MODAL panel: no scrim, and the full-viewport wrapper becomes
   * `pointer-events-none` so the app beside the panel stays fully clickable (the transparent wrapper
   * would otherwise swallow every click). Non-modal panels have NO outside-click close — they close
   * via their own UI, the toggle that opened them, or Escape — and drop `aria-modal` (the rest of the
   * app is NOT inert). Escape is also ignored while a real modal (`[aria-modal="true"]`) is stacked
   * above, so one keypress doesn't close a dialog AND the panel underneath. Default true = modal
   * scrim behavior, unchanged for existing callers.
   */
  showScrim?: boolean;
  /** `true` drops the rounded left corners (the Figma Accounts panel is square). */
  square?: boolean;
  children: ReactNode;
}

const TRANSITION_MS = 300;

/**
 * Right-anchored slide-over drawer. The codebase has no animation lib, so the slide is pure CSS:
 * we keep the panel mounted through the exit transition and toggle a `translate-x` / `opacity`
 * class one frame after mount (and again on close), unmounting on `transitionend`.
 *
 * Mirrors the portal + scrim + Escape-to-close behaviour of {@link Dialog}. All colours come from
 * theme tokens so it works in dark + light. Reusable for any future drawer — including non-modal
 * persistent panels via `showScrim={false}` (see the prop docs).
 */
export function SlideOverPanel({
  open,
  onClose,
  ariaLabel,
  maxWidthPx = 879,
  surfaceClassName = 'bg-canvas-inset',
  insetTopPx = 0,
  showScrim = true,
  square = false,
  children,
}: Props) {
  // `mounted` keeps the node in the DOM during the exit animation; `entered` drives the transform.
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (open) {
      if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
      setMounted(true);
      // Next frame: flip to the entered position so the browser animates the transition.
      const raf = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(raf);
    }
    // Closing: play the exit transition, then unmount.
    setEntered(false);
    closeTimer.current = setTimeout(() => setMounted(false), TRANSITION_MS);
    return () => { if (closeTimer.current) clearTimeout(closeTimer.current); };
  }, [open]);

  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // An inner layer (a `Popover` inside the panel, 2026-10-14) already took this Escape: one
      // keypress closes one layer, the next one closes the panel.
      if (e.defaultPrevented) return;
      // Non-modal: a real modal (connect dialog, …) may be stacked above — let ITS Escape handler
      // win instead of also closing the panel underneath.
      if (!showScrim && document.querySelector('[aria-modal="true"]')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mounted, onClose, showScrim]);

  if (!mounted) return null;

  return createPortal(
    <div className={`fixed inset-0 z-50 ${showScrim ? '' : 'pointer-events-none'}`} role="presentation">
      {/* Scrim fades with the panel (modal mode only — it is also the outside-click close target). */}
      {showScrim && (
        <div
          onClick={onClose}
          className="absolute inset-0 transition-opacity duration-300 ease-out"
          style={{ background: 'var(--scrim)', opacity: entered ? 1 : 0 }}
        />
      )}
      <div
        role="dialog"
        aria-modal={showScrim ? 'true' : undefined}
        aria-label={ariaLabel}
        onClick={e => e.stopPropagation()}
        className={`absolute right-0 flex w-full flex-col overflow-y-auto ${square ? '' : 'rounded-l-xl'} ${
          showScrim ? '' : 'pointer-events-auto'
        } ${surfaceClassName} shadow-card transition-transform duration-300 ease-out ${
          entered ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ maxWidth: maxWidthPx, top: insetTopPx, height: `calc(100% - ${insetTopPx}px)` }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
